import { useEffect, useId, useState, type FormEvent } from 'react';
import { formatEuro } from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  FieldGroup,
  RadioCard,
  Select,
  Skeleton,
  TextField,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { addUnit, changeUnit, useCreateOptions, useUnitChangePreview } from '../api';
import type { DealRecord, DealUnitsResponse, UnitChangeReason } from '../types';
import styles from '../dialogs/dialogs.module.css';

/**
 * Deal-Akte 04: „Einheit wechseln" (new unit, mandatory reason, the consequences of the change preview →
 * `POST /deals/:id/units/change`) and „Einheit hinzufügen" (`POST /deals/:id/units`).
 */

const REASONS: { value: UnitChangeReason; label: string }[] = [
  { value: 'not_financeable', label: 'Nicht finanzierbar' },
  { value: 'location_preference', label: 'Standortwunsch Kunde' },
  { value: 'price', label: 'Preis' },
  { value: 'other', label: 'Sonstiges' },
];

export interface UnitChangeDialogProps {
  open: boolean;
  dealId: string;
  /** The unit that is replaced (the deal's apartment). */
  fromUnit: { id: string; number: string } | null;
  alternatives: DealUnitsResponse['alternatives'];
  onClose: () => void;
  onDone: (deal: DealRecord) => void;
}

export function UnitChangeDialog({
  open,
  dealId,
  fromUnit,
  alternatives,
  onClose,
  onDone,
}: UnitChangeDialogProps) {
  const formId = useId();
  const [toUnitId, setToUnitId] = useState('');
  const [reason, setReason] = useState<UnitChangeReason>('not_financeable');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const preview = useUnitChangePreview(dealId, open && toUnitId ? toUnitId : null, fromUnit?.id ?? null);

  useEffect(() => {
    if (!open) return;
    setToUnitId(alternatives[0]?.id ?? '');
    setReason('not_financeable');
    setNote('');
    setErrors({});
    setBanner(null);
  }, [open, alternatives]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (toUnitId === '') {
      setErrors({ toUnitId: 'Bitte wählen Sie eine Einheit.' });
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      const result = await changeUnit(dealId, {
        toUnitId,
        fromUnitId: fromUnit?.id,
        reason,
        note: note.trim() === '' ? null : note.trim(),
      });
      onDone(result.deal);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0)
        setErrors(error.fields);
      else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  const title = fromUnit
    ? `${fromUnit.number} durch eine andere Einheit ersetzen`
    : 'Eine andere Einheit wählen';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow="Einheit wechseln"
      title={title}
      initialFocus="container"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={alternatives.length === 0}>
            Einheit wechseln
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.createForm} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {alternatives.length === 0 ? (
          <Banner tone="neutral">Im Objekt ist keine weitere Einheit frei.</Banner>
        ) : null}
        <Select
          label="Neue Einheit"
          required
          value={toUnitId}
          placeholder="Bitte wählen"
          error={errors['toUnitId']}
          labelEnd={toUnitId ? <span className={styles.unitStatus}>Frei</span> : undefined}
          options={alternatives.map((unit) => ({ value: unit.id, label: unit.label }))}
          onChange={(event) => setToUnitId(event.target.value)}
        />
        <FieldGroup legend="Grund" required columns={2}>
          {REASONS.map((entry) => (
            <RadioCard
              key={entry.value}
              name={`${formId}-reason`}
              value={entry.value}
              title={entry.label}
              checked={reason === entry.value}
              onChange={() => setReason(entry.value)}
            />
          ))}
        </FieldGroup>
        {reason === 'other' ? (
          <TextField label="Notiz" value={note} onChange={(event) => setNote(event.target.value)} />
        ) : null}
        {toUnitId ? (
          preview.isPending ? (
            <div className={styles.consequences}>
              <Skeleton width="80%" />
              <Skeleton width="70%" />
              <Skeleton width="60%" />
            </div>
          ) : preview.isError ? (
            <Banner tone="danger">{errorMessage(preview.error)}</Banner>
          ) : preview.data ? (
            <div className={styles.consequences} role="status">
              {preview.data.consequences.map((line) => (
                <div key={line}>{line}</div>
              ))}
              {preview.data.commissionBeforeCents !== null &&
              preview.data.commissionAfterCents !== null &&
              !preview.data.consequences.some((line) => line.startsWith('Provision')) ? (
                <div>
                  Provision wird neu berechnet: {formatEuro(preview.data.commissionAfterCents)} statt{' '}
                  {formatEuro(preview.data.commissionBeforeCents)}.
                </div>
              ) : null}
            </div>
          ) : null
        ) : null}
      </form>
    </Dialog>
  );
}

export interface AddUnitDialogProps {
  open: boolean;
  dealId: string;
  /** The free units of the deal's property; a deal without a property offers every property's free units. */
  alternatives: DealUnitsResponse['alternatives'];
  contactId: string;
  /** „Einheit wählen" for a deal without a unit, „Einheit hinzufügen" otherwise. */
  first: boolean;
  onClose: () => void;
  onDone: (deal: DealRecord) => void;
}

export function AddUnitDialog({
  open,
  dealId,
  alternatives,
  contactId,
  first,
  onClose,
  onDone,
}: AddUnitDialogProps) {
  const formId = useId();
  const [unitId, setUnitId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const fallback = useCreateOptions(open && alternatives.length === 0 ? contactId : null);
  const options: { id: string; label: string }[] =
    alternatives.length > 0
      ? alternatives
      : (fallback.data?.properties ?? []).flatMap((property) =>
          property.units
            .filter((unit) => unit.availability === 'available')
            .map((unit) => ({ id: unit.id, label: `${unit.label} · ${property.name}` })),
        );

  useEffect(() => {
    if (!open) return;
    setUnitId('');
    setError(null);
    setBanner(null);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (unitId === '') {
      setError('Bitte wählen Sie eine Einheit.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      onDone(await addUnit(dealId, unitId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.fields['unitId'])
        setError(err.fields['unitId']);
      else setBanner(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  const label = first ? 'Einheit wählen' : 'Einheit hinzufügen';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={label}
      title={first ? 'Einheit für den Deal wählen' : 'Weitere Einheit an den Deal hängen'}
      subtitle={
        first
          ? 'Der Deal geht damit auf „Einheit gewählt“, sofern die Stage konfiguriert ist.'
          : 'Der Kaufpreis wird summiert; die Einheit bleibt frei, bis reserviert wird.'
      }
      initialFocus="container"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={options.length === 0}>
            {label}
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.createForm} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {options.length === 0 && !fallback.isPending ? (
          <Banner tone="neutral">Im Objekt ist keine Einheit frei.</Banner>
        ) : null}
        <Select
          label="Einheit"
          required
          value={unitId}
          placeholder="Bitte wählen"
          error={error ?? undefined}
          options={options.map((unit) => ({ value: unit.id, label: unit.label }))}
          onChange={(event) => setUnitId(event.target.value)}
        />
      </form>
    </Dialog>
  );
}
